import type React from "react";
import { Pencil } from "lucide-react";
import { UserAvatar } from "@/components/ui/user-avatar";
import {
  formatDocumentHistoryExactTime,
  formatDocumentHistoryRelativeTime,
} from "@/domains/documents/history/documentHistoryPresentation";
import {
  historyActor,
  historyActorName,
  type HistoryActorEntry,
  type HistoryRelativeTimeCopy,
} from "./historyActor";

/**
 * Shared visual pieces for the value-history timelines: the per-attribute
 * modal (AttributeHistoryModal) and the building-wide modal
 * (BuildingDataHistoryModal) render the same rail + card nodes; the inspector's
 * HISTORY section and the building-wide modal render the same avatar rows.
 */

/** Rail dot colour for confirmed user edits. */
const HISTORY_USER_DOT_COLOR = "hsl(155 63% 39%)";

/** 36px avatar with the blue pencil badge marking a user edit. */
export function EditAvatar(props: { entry: HistoryActorEntry; unknownUser: string }) {
  return (
    <div className="relative mt-0.5 h-9 w-9 flex-none">
      <UserAvatar user={historyActor(props.entry, props.unknownUser)} size={36} />
      <span
        className="absolute -bottom-1 -right-1 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-white bg-blue-600 text-white"
        aria-hidden="true"
      >
        <Pencil className="h-2.5 w-2.5" />
      </span>
    </div>
  );
}

/** "Name edited · relative time" line, exact timestamp on hover. */
export function ActorSummary(props: {
  entry: HistoryActorEntry;
  language: string;
  unknownUser: string;
  relativeTimeCopy: HistoryRelativeTimeCopy;
}) {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-3 text-sm text-neutral-700">
      <p className="min-w-0 truncate">
        <span className="font-semibold text-neutral-900">
          {historyActorName(props.entry, props.unknownUser)}
        </span>{" "}
        {props.relativeTimeCopy.verbs.edited}
      </p>
      <time
        dateTime={props.entry.createdAt}
        title={formatDocumentHistoryExactTime(props.entry.createdAt, props.language)}
        className="flex-none text-xs font-medium tabular-nums text-neutral-600"
      >
        {formatDocumentHistoryRelativeTime(
          props.entry.createdAt,
          props.relativeTimeCopy.relativeTime
        )}
      </time>
    </div>
  );
}

export interface HistoryEditor {
  id: number | null;
  name: string | null;
  avatarUrl: string | null;
  color: string | null;
}

interface HistoryTimelineItemProps {
  /** Formatted date line (see formatHistoryDate). */
  dateText: string;
  /** Optional attribute-label line above the value (building-wide modal). */
  attributeLabel?: string;
  /** The value node (already localized / unit-suffixed by the caller). */
  value: React.ReactNode;
  editor: HistoryEditor;
  /** i18n strings: the "Edited by" label and the unknown-user fallback. */
  labels: { editedBy: string; unknownUser: string };
  isLast: boolean;
  /** When set, the card becomes clickable (e.g. drill into the attribute's own history). */
  onClick?: () => void;
  /** Extra content below the editor row (e.g. supporting-source chips). */
  children?: React.ReactNode;
}

/** One rail + card node of a value-history timeline. */
export function HistoryTimelineItem({
  dateText,
  attributeLabel,
  value,
  editor,
  labels,
  isLast,
  onClick,
  children,
}: HistoryTimelineItemProps) {
  const interactive = Boolean(onClick);
  return (
    <div style={{ display: "flex", gap: 12 }}>
      {/* rail */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: 12,
          flex: "0 0 12px",
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            background: HISTORY_USER_DOT_COLOR,
            marginTop: 5,
            flex: "0 0 auto",
          }}
        />
        {!isLast && (
          <span style={{ width: 2, flex: 1, background: "var(--bd-line)", marginTop: 2 }} />
        )}
      </div>

      {/* card */}
      <div
        onClick={onClick}
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        onKeyDown={
          interactive
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick?.();
                }
              }
            : undefined
        }
        onMouseEnter={
          interactive
            ? (e) => {
                e.currentTarget.style.background = "var(--bd-bg)";
              }
            : undefined
        }
        onMouseLeave={
          interactive
            ? (e) => {
                e.currentTarget.style.background = "transparent";
              }
            : undefined
        }
        style={{
          flex: 1,
          paddingBottom: isLast ? 0 : 16,
          minWidth: 0,
          cursor: interactive ? "pointer" : "default",
          borderRadius: 6,
          transition: "background 120ms",
        }}
      >
        <div style={{ fontSize: 11, color: "var(--bd-ink-4)" }}>{dateText}</div>

        {attributeLabel && (
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--bd-ink-2)", marginTop: 2 }}>
            {attributeLabel}
          </div>
        )}

        <div
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: "var(--bd-ink)",
            marginTop: 2,
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {value}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 4,
            fontSize: 12,
            color: "var(--bd-ink-3)",
          }}
        >
          {editor.id ? (
            <UserAvatar
              user={{
                id: editor.id,
                email: editor.name ?? labels.unknownUser,
                first_name: (editor.name ?? "").split(" ")[0] ?? null,
                last_name: (editor.name ?? "").split(" ").slice(1).join(" ") || null,
                avatar_url: editor.avatarUrl ?? null,
                color: editor.color ?? null,
              }}
              size={16}
            />
          ) : null}
          <span>
            {labels.editedBy}: {editor.name || labels.unknownUser}
          </span>
        </div>

        {children}
      </div>
    </div>
  );
}
